import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { CUSTOM_VOICE_MAX_SECONDS } from '@goaltree/backend/convex/lib/coach'
import { Dialog, DialogContent } from '@goaltree/ui/components/dialog'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useMutation } from 'convex/react'
import { ConvexError } from 'convex/values'
import { Loader2, Mic, Square } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

// Encode a decoded AudioBuffer (mono, downmixed) to a 16-bit PCM WAV Blob. xAI's
// custom-voices API wants WAV/MP3, but MediaRecorder emits webm/mp4 — so we
// decode the recording and re-encode WAV in the browser before uploading.
function encodeWav(buffer: AudioBuffer): Blob {
  const numCh = buffer.numberOfChannels
  const len = buffer.length
  const sampleRate = buffer.sampleRate
  // Downmix to mono — a single speaker clone doesn't need stereo, and it halves
  // the upload.
  const mono = new Float32Array(len)
  for (let ch = 0; ch < numCh; ch++) {
    const data = buffer.getChannelData(ch)
    for (let i = 0; i < len; i++) mono[i] += data[i] / numCh
  }

  const bytesPerSample = 2
  const dataSize = len * bytesPerSample
  const ab = new ArrayBuffer(44 + dataSize)
  const view = new DataView(ab)
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i))
  }
  writeStr(0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeStr(8, 'WAVE')
  writeStr(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, 1, true) // mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * bytesPerSample, true)
  view.setUint16(32, bytesPerSample, true)
  view.setUint16(34, 16, true)
  writeStr(36, 'data')
  view.setUint32(40, dataSize, true)
  let offset = 44
  for (let i = 0; i < len; i++) {
    const s = Math.max(-1, Math.min(1, mono[i]))
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
    offset += bytesPerSample
  }
  return new Blob([ab], { type: 'audio/wav' })
}

type Phase = 'idle' | 'recording' | 'recorded' | 'uploading'

export function CoachVoiceRecorder({
  open,
  onOpenChange,
  onCloned,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCloned: () => void
}) {
  const { t, i18n } = useTranslation()
  const generateUploadUrl = useMutation(api.preferences.generateCustomVoiceUploadUrl)
  const createCustomVoice = useAction(api.voice.createCustomVoice)

  const [phase, setPhase] = useState<Phase>('idle')
  const [seconds, setSeconds] = useState(0)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const wavRef = useRef<Blob | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function cleanup() {
    if (tickRef.current) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop()
      } catch {
        // already stopped
      }
    }
    recorderRef.current = null
    streamRef.current?.getTracks().forEach((tr) => tr.stop())
    streamRef.current = null
  }

  // Tear everything down when the dialog closes, and on unmount.
  useEffect(() => {
    if (!open) {
      cleanup()
      setPhase('idle')
      setSeconds(0)
      chunksRef.current = []
      wavRef.current = null
    }
    return cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function startRecording() {
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      toast.error(t('coach.micDenied'))
      return
    }
    streamRef.current = stream
    chunksRef.current = []
    wavRef.current = null
    setSeconds(0)

    const rec = new MediaRecorder(stream)
    recorderRef.current = rec
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }
    rec.onstop = async () => {
      streamRef.current?.getTracks().forEach((tr) => tr.stop())
      streamRef.current = null
      const raw = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' })
      try {
        const AudioCtx =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        const ctx = new AudioCtx()
        const decoded = await ctx.decodeAudioData(await raw.arrayBuffer())
        await ctx.close().catch(() => {})
        wavRef.current = encodeWav(decoded)
        setPhase('recorded')
      } catch {
        toast.error(t('coach.cloneError'))
        setPhase('idle')
      }
    }
    rec.start()
    setPhase('recording')
    tickRef.current = setInterval(() => {
      setSeconds((s) => {
        const next = s + 1
        if (next >= CUSTOM_VOICE_MAX_SECONDS) stopRecording()
        return next
      })
    }, 1000)
  }

  function stopRecording() {
    if (tickRef.current) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop()
    }
  }

  async function useThisVoice() {
    if (!wavRef.current) return
    if (seconds < 3) {
      toast.error(t('coach.recordTooShort'))
      return
    }
    setPhase('uploading')
    try {
      const uploadUrl = await generateUploadUrl({})
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'audio/wav' },
        body: wavRef.current,
      })
      if (!res.ok) throw new Error('upload failed')
      const { storageId } = (await res.json()) as { storageId: Id<'_storage'> }
      await createCustomVoice({ storageId, locale: i18n.language })
      toast.success(t('coach.voiceReady'))
      onCloned()
      onOpenChange(false)
    } catch (err) {
      const msg =
        err instanceof ConvexError
          ? String((err as ConvexError<string>).data)
          : t('coach.cloneError')
      toast.error(msg)
      setPhase('recorded')
    }
  }

  const pct = Math.min(100, (seconds / CUSTOM_VOICE_MAX_SECONDS) * 100)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <div className="flex flex-col gap-4 pt-2">
          <div className="space-y-1">
            <h2 className="text-lg font-bold">{t('coach.cloneTitle')}</h2>
            <p className="text-sm text-muted-foreground">{t('coach.cloneSubtitle')}</p>
          </div>

          <blockquote className="rounded-lg border border-border/60 bg-muted/30 p-4 text-sm font-medium leading-relaxed">
            “{t('coach.clonePhrase')}”
          </blockquote>

          <div className="flex flex-col items-center gap-3 py-2">
            {phase === 'recording' && (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary transition-[width] duration-1000 ease-linear"
                  style={{ width: `${pct}%` }}
                />
              </div>
            )}
            <div className="text-2xl font-bold tabular-nums">
              {String(Math.floor(seconds / 60)).padStart(1, '0')}:
              {String(seconds % 60).padStart(2, '0')}
            </div>

            {phase === 'idle' && (
              <button
                type="button"
                onClick={startRecording}
                className="flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
              >
                <Mic className="h-4 w-4" />
                {t('coach.startRecording')}
              </button>
            )}

            {phase === 'recording' && (
              <button
                type="button"
                onClick={stopRecording}
                className="flex cursor-pointer items-center gap-2 rounded-full bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                <Square className="h-3.5 w-3.5 fill-current" />
                {t('coach.stopRecording')}
              </button>
            )}

            {(phase === 'recorded' || phase === 'uploading') && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={phase === 'uploading'}
                  onClick={() => {
                    setPhase('idle')
                    setSeconds(0)
                  }}
                  className={cn(
                    'cursor-pointer rounded-full border border-border/60 px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground',
                    phase === 'uploading' && 'pointer-events-none opacity-50',
                  )}
                >
                  {t('coach.rerecord')}
                </button>
                <button
                  type="button"
                  disabled={phase === 'uploading'}
                  onClick={useThisVoice}
                  className="flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-70"
                >
                  {phase === 'uploading' && <Loader2 className="h-4 w-4 animate-spin" />}
                  {phase === 'uploading' ? t('coach.cloning') : t('coach.useThisVoice')}
                </button>
              </div>
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground/70">{t('coach.cloneTip')}</p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
